import React from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/sections/Hero';
import Button from '../components/common/Button';
import Reveal from '../components/fx/Reveal';
import Counter from '../components/fx/Counter';
import TiltCard from '../components/fx/TiltCard';
import ScrollProgress from '../components/fx/ScrollProgress';
import FaqSection from '../components/sections/FaqSection';
import { ServicesGrid, StackMatrix, EngagementModels, ProductsShowcase, TestimonialsBlock } from '../components/sections/ContentBlocks';
import { MARQUEE_TECH, HOME_FAQS } from '../data/siteContent';
import { useProducts } from '../hooks/useContent';

/* ─────────────────────────────────────────────────────────────
   DATA
───────────────────────────────────────────────────────────── */

const CAPABILITIES = [
  {
    number: '01',
    title: 'Enterprise Software Architecture',
    body: 'End-to-end design and delivery of complex enterprise systems — ERP platforms, multi-module business applications, and operational infrastructure engineered for reliability, auditability, and long-term maintainability.',
    link: '/solutions/enterprise-software',
  },
  {
    number: '02',
    title: 'Application & Product Engineering',
    body: 'Bespoke software built around your exact operational requirements — not adapted from a generic template. Full-cycle delivery: requirements, architecture, development, testing, and production deployment.',
    link: '/solutions/custom-software',
  },
  {
    number: '03',
    title: 'SaaS Product Engineering',
    body: 'Multi-tenant SaaS platforms designed from the ground up with correct data isolation, subscription billing, API-first architecture, and the operational instrumentation enterprise buyers require.',
    link: '/solutions/saas-development',
  },
  {
    number: '04',
    title: 'AI & Intelligent Systems',
    body: 'Production AI/ML systems — predictive analytics, NLP pipelines, computer vision, intelligent automation — integrated into existing enterprise infrastructure with MLOps discipline and drift monitoring.',
    link: '/solutions/ai-ml-solutions',
  },
];

const PRINCIPLES = [
  {
    title: 'System Design Before Code',
    body: 'Every engagement begins with a documented architecture blueprint — data models, service boundaries, API contracts — reviewed and approved before a line of production code is written.',
  },
  {
    title: 'Performance Is Designed In',
    body: 'Query optimisation, caching strategy, and scalability constraints are addressed at the architecture stage, not after performance problems surface in production.',
  },
  {
    title: 'Observability From Day One',
    body: 'Structured logging, distributed tracing, and health metrics are built into the system from the first sprint, not added as an afterthought before deployment.',
  },
  {
    title: 'Long-Term Maintainability',
    body: 'Clean module boundaries, enforced service contracts, and maintained documentation ensure the system remains comprehensible — and extensible — years after initial delivery.',
  },
];

const DELIVERY_STEPS = [
  {
    step: '01',
    title: 'Technical Discovery',
    body: 'Structured requirements analysis, constraint identification, integration mapping, and domain modelling. Output: a written technical brief that scopes the engagement without ambiguity.',
  },
  {
    step: '02',
    title: 'Architecture Blueprinting',
    body: 'System design document covering data model, service architecture, API contracts, security model, and infrastructure plan. Client review and sign-off before development begins.',
  },
  {
    step: '03',
    title: 'Iterative Engineering',
    body: 'Sprint-based development with working software demonstrated at each milestone. Architecture decisions documented. Test coverage maintained throughout. No surprises at handover.',
  },
  {
    step: '04',
    title: 'Performance & Optimisation',
    body: 'Load testing, query analysis, caching validation, and security review before production release. Deployment documentation and operational runbook included as standard.',
  },
];

/* ─────────────────────────────────────────────────────────────
   EXTRA DATA (visual layer)
───────────────────────────────────────────────────────────── */

const buildStats = (productCount) => [
  { to: 30, suffix: '+', label: 'Technologies we build with' },
  { to: 12, suffix: '', label: 'Services under one roof' },
  productCount > 0
    ? { to: productCount, suffix: '', label: 'Products in our pipeline' }
    : { to: 4, suffix: '', label: 'Flexible engagement models' },
  { to: 100, suffix: '%', label: 'Code & IP owned by you' },
];

const INDUSTRY_LIST = [
  'Healthcare & Clinical Systems',
  'Financial Services & FinTech',
  'Manufacturing & ERP',
  'Education & EdTech',
  'Enterprise SaaS',
  'Logistics & Supply Chain',
  'Retail & E-Commerce',
  'Startups & MVPs',
  'Real Estate & PropTech',
  'Travel & Hospitality',
  'Media & Entertainment',
  'Your industry',
];

const ICONS = [
  <svg key="i1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V7l9-4 9 4v14" /><path d="M9 21v-6h6v6M8 10h.01M12 10h.01M16 10h.01" /></svg>,
  <svg key="i2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M8 9l-4 3 4 3M16 9l4 3-4 3M13.5 6l-3 12" /></svg>,
  <svg key="i3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l9 5-9 5-9-5 9-5z" /><path d="M3 13l9 5 9-5M3 17.5l9 5 9-5" /></svg>,
  <svg key="i4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" /></svg>,
];

/* ─────────────────────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────────────────────── */

function SectionHead({ label, title, sub, id }) {
  return (
    <Reveal as="header" className="qd-head">
      <span className="qd-label">{label}</span>
      <h2 className="qd-title" id={id}>{title}</h2>
      {sub && <p className="qd-sub">{sub}</p>}
    </Reveal>
  );
}

function Home() {
  const { products } = useProducts();
  const STATS = buildStats(products.length);
  return (
    <div className="qd">
      <ScrollProgress />
      {/* ── 1. Hero (3D) ───────────────────────────────────────── */}
      <Hero />

      {/* ── 2. Tech marquee ────────────────────────────────────── */}
      <div className="qd-marquee" id="after-hero" aria-label="Technologies we work with">
        <div className="qd-marquee__track">
          {[...MARQUEE_TECH, ...MARQUEE_TECH].map((t, i) => (
            <span key={i} className="qd-marquee__item" aria-hidden={i >= MARQUEE_TECH.length}>
              <i aria-hidden="true" />{t}
            </span>
          ))}
        </div>
      </div>

      {/* ── 3. Stats ───────────────────────────────────────────── */}
      <section className="qd-stats" aria-label="Key figures">
        <div className="qd-wrap qd-stats__grid">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 90} className="qd-stat">
              <span className="qd-stat__val"><Counter to={s.to} suffix={s.suffix} /></span>
              <span className="qd-stat__lbl">{s.label}</span>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── 4. All services ────────────────────────────────────── */}
      <ServicesGrid />

      {/* ── 4b. Our products ───────────────────────────────────── */}
      <ProductsShowcase />

      {/* ── 5. Signature practices ─────────────────────────────── */}
      <section className="qd-section qd-section--tint" aria-labelledby="capabilities-heading">
        <div className="qd-wrap">
          <SectionHead
            label="Signature Practices"
            id="capabilities-heading"
            title="Our Engineering Focus"
            sub="Our core engineering focus areas — each delivered with documented architecture, defined milestones and full IP transfer."
          />
          <div className="qd-caps">
            {CAPABILITIES.map(({ number, title, body, link }, i) => (
              <Reveal key={number} delay={i * 100}>
                <TiltCard className="qd-card">
                  <span className="qd-card__icon">{ICONS[i]}</span>
                  <span className="qd-card__num" aria-hidden="true">{number}</span>
                  <h3 className="qd-card__title">{title}</h3>
                  <p className="qd-card__body">{body}</p>
                  <Link to={link} className="qd-card__link" aria-label={`Learn more about ${title}`}>
                    Explore <span aria-hidden="true">→</span>
                  </Link>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. Technology matrix ───────────────────────────────── */}
      <StackMatrix />

      {/* ── 7. Approach ────────────────────────────────────────── */}
      <section className="qd-section" aria-labelledby="philosophy-heading">
        <div className="qd-wrap qd-approach">
          <Reveal className="qd-approach__intro">
            <span className="qd-label">Why Quantum Stack</span>
            <h2 className="qd-title" id="philosophy-heading">
              Built Right.<br />Shipped Fast.
            </h2>
            <p className="qd-sub">
              Every solution starts with <strong>your requirements</strong> — not a template.
              We listen first, then engineer software that fits your business, your users and your budget.
            </p>
            <p className="qd-sub">
              Speed without structure creates expensive rebuilds. Structure without speed
              loses markets. We give you both — a clear architecture up front, then
              short sprints that put working software in your hands every couple of weeks.
            </p>
            <p className="qd-sub">
              What you can expect: transparent pricing, senior engineers on every project,
              and code you fully own.
            </p>
            <div className="qd-approach__cta">
              <Button to="/contact" size="lg">Get a Free Consultation</Button>
            </div>
          </Reveal>
          <div className="qd-principles">
            {PRINCIPLES.map(({ title, body }, i) => (
              <Reveal key={title} delay={i * 90}>
                <TiltCard className="qd-card qd-card--sm" max={6}>
                  <h3 className="qd-card__title">{title}</h3>
                  <p className="qd-card__body">{body}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── 8. Engagement models ───────────────────────────────── */}
      <EngagementModels />

      {/* ── 9. Delivery timeline ───────────────────────────────── */}
      <section className="qd-section qd-section--tint" aria-labelledby="delivery-heading">
        <div className="qd-wrap">
          <SectionHead
            label="How We Work"
            id="delivery-heading"
            title="A Simple Process From Idea to Launch"
            sub="A repeatable four-stage process that removes ambiguity, prevents scope drift and gets working software in front of users sooner."
          />
          <ol className="qd-timeline" role="list">
            {DELIVERY_STEPS.map(({ step, title, body }, i) => (
              <Reveal as="li" key={step} delay={i * 80} className="qd-tl">
                <span className="qd-tl__node" aria-label={`Step ${step}`}>{step}</span>
                <div className="qd-tl__panel">
                  <h3 className="qd-card__title">{title}</h3>
                  <p className="qd-card__body">{body}</p>
                </div>
              </Reveal>
            ))}
          </ol>
          <div className="qd-center">
            <Button to="/our-process" variant="outline-light">See the Full Process</Button>
          </div>
        </div>
      </section>

      {/* ── 10. Industries ─────────────────────────────────────── */}
      <section className="qd-section" aria-labelledby="industries-heading">
        <div className="qd-wrap">
          <SectionHead
            label="Industries"
            id="industries-heading"
            title="We Build for Any Domain"
            sub="Wherever there is a problem worth solving with software, we ramp up on your domain quickly. A few of the sectors we are open to building for:"
          />
          <ul className="qd-pills" role="list">
            {INDUSTRY_LIST.map((label, i) => (
              <Reveal as="li" key={label} delay={(i % 6) * 60} className="qd-pill">{label}</Reveal>
            ))}
          </ul>
          <div className="qd-center">
            <Button to="/industries" variant="outline-light">Explore Industries</Button>
          </div>
        </div>
      </section>

      {/* ── 10b. Client feedback (hidden until admin adds some) ── */}
      <TestimonialsBlock />

      {/* ── 11. FAQ ────────────────────────────────────────────── */}
      <FaqSection heading="Frequently Asked Questions" faqs={HOME_FAQS} />

      {/* ── 12. Reach ──────────────────────────────────────────── */}
      <section className="qd-section qd-section--tint" aria-labelledby="positioning-heading">
        <div className="qd-wrap qd-reach">
          <Reveal>
            <span className="qd-label">Location &amp; Reach</span>
            <h2 className="qd-title" id="positioning-heading">
              Based in Pune, India.<br />Building for the world.
            </h2>
            <p className="qd-sub">
              Quantum Stack is a software engineering company founded in 2024, operating from Pimpri-Chinchwad, Pune —
              one of India's most active technology corridors. We are remote-first and
              ready to work with startups, growth-stage companies and enterprises in
              any country and time zone.
            </p>
          </Reveal>
          <Reveal delay={120} className="qd-globe" aria-hidden="true">
            <span className="qd-globe__ring qd-globe__ring--1" />
            <span className="qd-globe__ring qd-globe__ring--2" />
            <span className="qd-globe__ring qd-globe__ring--3" />
            <span className="qd-globe__core">Pune<small>India HQ · Global delivery</small></span>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Home;
